import React, { useMemo } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetOccurrenceEditFormQuery } from "../../../Api/attoDeskApi";

/**
 * Resolver for /class-sessions/:occurrenceId/edit.
 *
 * The URL id is a class_occurrences row (NOT a class_schedules row and NOT an
 * assign_classes row). To render the shared Class edit form we need the parent
 * class id. Resolve it via GET /class-schedule/{id}/edit-form (returns both
 * class_id / class_schedule_id) and then hard-redirect to the canonical
 * edit route so the user lands on ONE shared UI.
 *
 * The redirect target is /classes/{class_id}/edit which renders the same
 * ClassForm the Create New Class page uses (mode="edit"). The occurrence id
 * is forwarded so the edit page can show the "Selected occurrence" context
 * band and use the occurrence date as the effective date.
 *
 * Errors are distinguished:
 *   - occurrence not found (404)   → "Class session not found"
 *   - occurrence found but no class → "This session exists, but its recurring
 *                                     class schedule could not be resolved."
 * Both offer a "Back to Class Sessions" action honouring `return_url`.
 */
const LIST_ROUTE = "/class-sessions/list";

const Index: React.FC = () => {
  const { occurrenceId = "" } = useParams<{ occurrenceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const returnUrl = useMemo(() => {
    const qp = new URLSearchParams(location.search).get("return_url");
    return qp && qp.startsWith("/") ? qp : LIST_ROUTE;
  }, [location.search]);

  const { data, isLoading, isError, error } = useGetOccurrenceEditFormQuery(
    occurrenceId,
    { skip: !occurrenceId }
  );
  const schedule = (data as any)?.data ?? null;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Genuine 404 / access / bad id — "occurrence not found".
  if (isError || !schedule) {
    const status = (error as any)?.status;
    const denied = status === 403;
    const invalid = !occurrenceId || Number.isNaN(Number(occurrenceId));
    const title = denied
      ? "Access denied"
      : invalid
        ? "Invalid session link"
        : "Class session not found";
    const detail = denied
      ? "You do not have permission to edit this class session."
      : invalid
        ? "The link is missing a session ID. Return to Class Sessions and try again."
        : "This class session may have been deleted or moved.";
    return (
      <ErrorPanel
        title={title}
        detail={detail}
        onBack={() => navigate(returnUrl)}
      />
    );
  }

  // Occurrence exists but we couldn't resolve its parent class template. This
  // is a data-integrity edge case (orphaned occurrence). Distinct message so
  // the user (and support) can tell it apart from a missing session.
  const classId = schedule.class_id ?? schedule.class_schedule_id ?? null;
  if (!classId) {
    return (
      <ErrorPanel
        title="Class schedule missing"
        detail="This session exists, but its recurring class schedule could not be resolved."
        onBack={() => navigate(returnUrl)}
      />
    );
  }

  // Redirect to the shared edit page. Forward the occurrence id (context
  // band + effective date) and the caller's return_url unchanged.
  const params = new URLSearchParams();
  params.set("occurrence_id", String(occurrenceId));
  params.set("return_url", returnUrl);
  return <Navigate to={`/classes/${classId}/edit?${params.toString()}`} replace />;
};

const ErrorPanel: React.FC<{ title: string; detail: string; onBack: () => void }> = ({
  title,
  detail,
  onBack,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "60vh",
      px: 2,
      textAlign: "center",
    }}
  >
    <ErrorOutlineIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
    <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 3, maxWidth: 460 }}>
      {detail}
    </Typography>
    <Button
      variant="contained"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
    >
      Back to Class Sessions
    </Button>
  </Box>
);

export default Index;
